export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';

export type Permission =
  // Organization Administration
  | 'organization.view'
  | 'organization.update'
  | 'organization.delete'
  | 'organization.transfer_ownership'
  // Team Management
  | 'members.view'
  | 'members.invite'
  | 'members.remove'
  | 'members.update_role'
  // Betting Operations
  | 'bets.view'
  | 'bets.create'
  | 'bets.update'
  | 'bets.settle'
  | 'bets.delete'
  | 'bankroll.view'
  | 'bankroll.update_settings'
  | 'bankroll.reset'
  // AI / Recommendation Operations
  | 'recommendations.view'
  | 'recommendations.release'
  | 'recommendations.approve'
  | 'calibration.view'
  | 'calibration.run'
  | 'calibration.promote'
  // Platform Administration
  | 'audit_log.view'
  | 'audit_log.view_all'
  | 'settings.view'
  | 'settings.update'
  // Billing
  | 'billing.view'
  | 'billing.manage';

const VIEWER_PERMISSIONS: readonly Permission[] = [
  'organization.view',
  'members.view',
  'bets.view',
  'bankroll.view',
  'recommendations.view',
  'calibration.view',
  'settings.view',
  'billing.view',
];

const MEMBER_PERMISSIONS: readonly Permission[] = [
  ...VIEWER_PERMISSIONS,
  'bets.create',
  'bets.update',
  'bets.settle',
  'calibration.run',
];

const ADMIN_PERMISSIONS: readonly Permission[] = [
  ...MEMBER_PERMISSIONS,
  'organization.update',
  'members.invite',
  'members.remove',
  'members.update_role',
  'bets.delete',
  'bankroll.update_settings',
  'bankroll.reset',
  'recommendations.release',
  'recommendations.approve',
  'calibration.promote',
  'audit_log.view',
  'settings.update',
];

const OWNER_PERMISSIONS: readonly Permission[] = [
  ...ADMIN_PERMISSIONS,
  'organization.delete',
  'organization.transfer_ownership',
  'audit_log.view_all',
  'billing.manage',
];

export const ROLE_PERMISSIONS: Readonly<Record<OrgRole, readonly Permission[]>> = {
  viewer: VIEWER_PERMISSIONS,
  member: MEMBER_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
  owner: OWNER_PERMISSIONS,
};

export function getPermissionsForRole(role: OrgRole): Permission[] {
  return [...(ROLE_PERMISSIONS[role] ?? [])];
}

export function checkPermission(permissions: readonly Permission[], permission: Permission): boolean {
  return permissions.includes(permission);
}

export function isValidRole(role: string): role is OrgRole {
  return ['owner', 'admin', 'member', 'viewer'].includes(role);
}
